'use client';

import { FC } from 'react';

import { BreakdownsResult, EMetric, metricDictionary } from 'api/useAnalytics';
import {
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  LabelList,
  Tooltip
} from 'recharts';
import { DateRange } from 'react-day-picker';

import { Card, CardContent, CardHeader } from 'components/ui/card';
import { ChartContainer } from 'components/ui/chart';

import { AnalyticsBoxHeader } from './AnalyticsBoxHeader/AnalyticsBoxHeader';
import { chartConfig } from './analyticsChart.config';
import { AnalyticsLoadingBox } from './AnalyticsLoadingBox';
import Decimal from 'decimal.js';
import { BarsTooltip } from './AnalyticsChartTooltip/BarsTooltip';
import { EStatisticType } from 'views/Statistics/StatisticsView.types';

type AnalyticsBarsBoxProps = {
  isLoading: boolean;
  metricName: EMetric;
  currentBreakdown: BreakdownsResult;
  comparedBreakdown?: BreakdownsResult;
  dateRange: DateRange;
  comparedDateRange?: DateRange;
};

const CATEGORY_GAP = 35;
const CATEGORY_GAP_WITH_COMPARISON = 60;

export const AnalyticsBarsBox: FC<AnalyticsBarsBoxProps> = ({
  isLoading,
  metricName,
  currentBreakdown,
  comparedBreakdown,
  dateRange,
  comparedDateRange
}) => {
  if (isLoading) return <AnalyticsLoadingBox />;

  const chartData = currentBreakdown.values.map((item) => {
    const prevItem = comparedBreakdown?.values.find(
      (prev) => prev.breakdownValue === item.breakdownValue
    );

    return {
      ...item,
      value:
        item.value instanceof Decimal ? item.value.toNumber() : item.value || 0,
      previousValue:
        prevItem?.value instanceof Decimal
          ? prevItem.value.toNumber()
          : prevItem?.value || 0
    };
  });
  const gap = comparedBreakdown ? CATEGORY_GAP_WITH_COMPARISON : CATEGORY_GAP;
  const height = comparedBreakdown
    ? chartData.length * gap > 175
      ? chartData.length * gap
      : 175
    : chartData.length * gap > 100
      ? chartData.length * gap
      : 100;
  return (
    <Card className="p-3">
      <CardHeader>
        <AnalyticsBoxHeader
          stat={currentBreakdown}
          isBreakdown={true}
          metricName={metricName}
          breakdownValues={chartData}
        />
      </CardHeader>
      <CardContent>
        <div className="max-h-[350px] overflow-y-auto overflow-x-hidden w-full">
          <div style={{ height: `${height}px` }}>
            <ChartContainer config={chartConfig} className="w-full h-full">
              <BarChart
                layout="vertical"
                margin={{ right: 50, left: 5, bottom: 5, top: 5 }}
                data={chartData}
                barSize={20}
                barGap={2}
                maxBarSize={20}
                height={height}
              >
                <defs>
                  <pattern
                    id="pattern-previous"
                    patternUnits="userSpaceOnUse"
                    width="6"
                    height="6"
                    patternTransform="rotate(45)"
                  >
                    <line
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="6"
                      stroke={chartConfig.previous.color}
                      strokeWidth="2"
                    />
                  </pattern>
                </defs>
                <CartesianGrid
                  vertical={false}
                  horizontal={false}
                  stroke="var(--base-border)"
                />
                <XAxis
                  type="number"
                  axisLine={false}
                  tickLine={false}
                  tick={false}
                  hide
                />
                <YAxis
                  dataKey="breakdownValue"
                  type="category"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: 'var(--base-muted-foreground)', fontSize: 12 }}
                  width={50}
                />
                <Tooltip
                  content={
                    <BarsTooltip
                      dateRange={dateRange}
                      comparedDateRange={comparedDateRange}
                      metricType={currentBreakdown.type}
                    />
                  }
                  cursor={false}
                />
                <Bar
                  dataKey="value"
                  fill={chartConfig.current.color}
                  name={`${metricDictionary[metricName].title}`}
                  radius={2}
                >
                  <LabelList
                    dataKey="value"
                    position="right"
                    fill="var(--base-muted-foreground)"
                    fontSize={12}
                    formatter={(value: number) => {
                      if (currentBreakdown.type === EStatisticType.CURRENCY) {
                        return value.toLocaleString('en-US', {
                          style: 'currency',
                          currency: 'USD'
                        });
                      }
                      return value;
                    }}
                  />
                </Bar>
                {comparedBreakdown && (
                  <Bar
                    dataKey="previousValue"
                    fill="url(#pattern-previous)"
                    name={'Compared period'}
                    radius={2}
                  >
                    <LabelList
                      dataKey="previousValue"
                      position="right"
                      fill="var(--base-muted-foreground)"
                      fontSize={12}
                      formatter={(value: number) => {
                        if (currentBreakdown.type === EStatisticType.CURRENCY) {
                          return value.toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD'
                          });
                        }
                        return value;
                      }}
                    />
                  </Bar>
                )}
              </BarChart>
            </ChartContainer>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
